import { createPortal } from "react-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { ProductVisual } from "./ProductVisual";
import { imageSrc, isPack, productImages, textureSrc } from "@/lib/catalog";

const PLACEHOLDER_VIEWS = [
  { id: "peca", label: "Peça" },
  { id: "tecido", label: "Tecido" },
  { id: "ambiente", label: "Ambiente" },
];

const PlaceholderView = ({ id, product, texture, design }) => {
  if (id === "peca" && design) return design;
  if (id === "peca") return <ProductVisual shape={product.shape} texture={texture} className="absolute inset-[10%]" />;
  const src = id === "tecido" ? textureSrc(texture) : "/img/demo/atelier-800.webp";
  return <img src={src} alt={id === "tecido" ? "Imagem provisória do tecido" : "Imagem provisória de ambiente"} className="absolute inset-0 h-full w-full object-cover" />;
};

const Frame = ({ children, tag }) => (
  <div className="relative aspect-[4/5] overflow-hidden rounded-3xl bg-linen lg:aspect-auto lg:h-[clamp(300px,calc(100vh-19rem),680px)] max-lg:aspect-auto max-lg:aspect-[4/5] max-lg:max-h-[58vh] max-lg:w-full max-lg:rounded-none" data-testid="product-gallery-main">
    {tag && <span className="bb-placeholder-tag">{tag}</span>}
    {children}
  </div>
);

const Fade = ({ k, children }) => (
  <AnimatePresence initial={false}>
    <motion.div key={k} className="absolute inset-0" initial={{ opacity: 0, scale: 1.03 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.div>
  </AnimatePresence>
);

const Thumbs = ({ items, active, onPick }) => (
  <div className="mt-3 grid grid-cols-4 gap-3 max-lg:hidden lg:grid-cols-8 lg:gap-2" role="tablist" aria-label="Vistas do produto">
    {items.map((t) => (
      <button key={t.key} type="button" role="tab" aria-selected={active === t.key} data-testid={`gallery-thumb-${t.key}`} onClick={() => onPick(t.key)} className={`relative aspect-square overflow-hidden rounded-xl bg-linen ${active === t.key ? "ring-2 ring-bordeaux" : "ring-1 ring-ink/10"}`}>
        {t.node}
        {t.label && <span className="absolute bottom-1 left-1 hidden rounded-full bg-ivory/90 px-1.5 py-0.5 text-[10px] text-ink xl:block">{t.label}</span>}
      </button>
    ))}
  </div>
);

// Fotografia inteira, em ecrã grande: toque/clique para ampliar, mover o rato (ou o dedo) para percorrer, setas para trocar, Esc para fechar.
const Lightbox = ({ photos, index, onClose, onIndex, productName }) => {
  const [zoom, setZoom] = useState(false);
  const [origin, setOrigin] = useState("50% 50%");
  const p = photos[index];
  const go = useCallback((d) => { setZoom(false); onIndex((index + d + photos.length) % photos.length); }, [index, photos.length, onIndex]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); else if (e.key === "ArrowRight" && photos.length > 1) go(1); else if (e.key === "ArrowLeft" && photos.length > 1) go(-1); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [go, onClose, photos.length]);
  const track = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setOrigin(`${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`);
  };
  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-[rgba(30,24,20,.92)]" role="dialog" aria-modal="true" aria-label={`Fotografia de ${productName}`} data-testid="gallery-lightbox" onClick={onClose}>
      <button type="button" onClick={onClose} aria-label="Fechar" data-testid="lightbox-close" className="absolute right-3 top-3 z-10 grid h-11 w-11 place-items-center rounded-full bg-ivory/90 text-ink"><X className="h-5 w-5" /></button>
      {photos.length > 1 && (
        <>
          <button type="button" onClick={(e) => { e.stopPropagation(); go(-1); }} aria-label="Fotografia anterior" className="absolute left-3 top-1/2 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-ivory/90 text-ink"><ChevronLeft className="h-5 w-5" /></button>
          <button type="button" onClick={(e) => { e.stopPropagation(); go(1); }} aria-label="Fotografia seguinte" className="absolute right-3 top-1/2 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-ivory/90 text-ink"><ChevronRight className="h-5 w-5" /></button>
        </>
      )}
      <div className="flex h-full w-full items-center justify-center overflow-hidden p-4 sm:p-10" onClick={(e) => e.stopPropagation()}>
        <img
          src={imageSrc(p.id, 1200)}
          alt={p.alt || productName}
          draggable={false}
          onClick={(e) => { track(e); setZoom((z) => !z); }}
          onMouseMove={zoom ? track : undefined}
          className={`max-h-full max-w-full select-none object-contain transition-transform duration-300 ${zoom ? "cursor-zoom-out" : "cursor-zoom-in"}`}
          style={{ transform: zoom ? "scale(2.4)" : "scale(1)", transformOrigin: origin }}
        />
      </div>
      <p className="pointer-events-none absolute inset-x-0 bottom-3 text-center text-xs text-ivory/80">{index + 1} / {photos.length} · {zoom ? "clica para reduzir" : "clica para ampliar"}</p>
    </div>,
    document.body,
  );
};

const FABRIC = "__tecido";
const DESIGN = "__desenho";

// Pré-visualização do tecido: ao escolher (ou pairar sobre) um padrão, a galeria mostra o tecido em grande.
const FabricView = ({ texture, name }) => (
  <>
    <img src={textureSrc(texture, 800)} alt={`Tecido ${name ?? ""}`.trim()} className="h-full w-full object-cover" data-testid="gallery-fabric-image" />
    {name && <span className="absolute bottom-3 left-3 rounded-full bg-ivory/95 px-3.5 py-1.5 text-xs font-medium text-ink" data-testid="gallery-fabric-caption">Tecido: {name}</span>}
  </>
);

const STICKY = "max-lg:-mx-5 lg:sticky lg:top-24";

export const Gallery = ({ product, texture, patternId, patternName, previewTexture, previewName, design, designs }) => {
  const photos = productImages(product, patternId);
  const [view, setView] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const first = useRef(true);
  const touchX = useRef(null);
  // Vistas do desenho: normalmente uma ("O teu desenho"); algumas peças têm mais (ex.: muda-fraldas fechado e aberto).
  const list = designs?.length ? designs : design ? [{ id: "d", label: "O teu desenho", node: design }] : [];
  const keyOf = (id) => `${DESIGN}:${id}`;
  const isDesignKey = (k) => typeof k === "string" && k.startsWith(DESIGN);
  const hasDesign = list.length > 0;
  // Ao mudar de padrão, volta ao desenho (ou às fotografias reais, se a peça não tiver desenho): vês logo o resultado.
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    setView((prev) => (isDesignKey(prev) ? prev : hasDesign ? keyOf(list[0].id) : null));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patternId, hasDesign]);
  if (photos.length === 0 && !hasDesign) {
    const active = PLACEHOLDER_VIEWS.some((v) => v.id === view) ? view : "peca";
    return (
      <div className={STICKY}>
        <Frame>
          <Fade k={`${active}-${texture}`}><PlaceholderView id={active} product={product} texture={texture} /></Fade>
        </Frame>
        <Thumbs active={active} onPick={setView} items={PLACEHOLDER_VIEWS.map((v) => ({ key: v.id, label: v.label, node: <div className="pointer-events-none absolute inset-0"><PlaceholderView id={v.id} product={product} texture={texture} /></div> }))} />
        <p className="mt-3 text-xs text-taupe">As minhas fotografias reais vão substituir estas imagens.</p>
      </div>
    );
  }
  // Sem simulação, o tecido nunca substitui as fotografias: vão direto às fotos reais.
  const hovering = !!previewTexture && hasDesign;
  // O desenho com o tecido escolhido é a vista principal (por omissão e ao pairar sobre um padrão).
  const showDesign = hasDesign && (hovering || isDesignKey(view) || (view === null && photos.length === 0) || (view !== null && view !== FABRIC && !photos.some((p) => p.id === view)));
  const showFabric = !showDesign && (hovering || (view === FABRIC && !!texture));
  const current = photos.find((p) => p.id === view) ?? photos[0];
  const activeDesign = list.find((d) => keyOf(d.id) === view) ?? list[0];
  const fabricTexture = previewTexture ?? texture;
  const fabricName = hovering ? previewName : patternName;
  const nextPhoto = () => {
    if (!current || showDesign || showFabric) return setView(photos[0]?.id ?? keyOf(list[0].id));
    const i = photos.findIndex((p) => p.id === current.id);
    setView(i + 1 < photos.length ? photos[i + 1].id : hasDesign ? keyOf(list[0].id) : photos[0].id);
  };
  // Comparar: da simulação para a fotografia original e vice-versa.
  const compare = () => setView(showDesign || showFabric ? photos[0].id : keyOf(list[0].id));
  const groups = [
    photos.length > 0 && { id: "foto", title: "Fotografias reais", items: photos.map((p, i) => ({ key: p.id, label: i === 0 ? "Original" : `Foto ${i + 1}`, node: <img src={imageSrc(p.id, 400)} alt="" loading="lazy" className="h-full w-full object-cover" /> })) },
    list.length > 0 && { id: "sim", title: "Simulação", items: list.map((d) => ({ key: keyOf(d.id), label: d.label, node: <div className="pointer-events-none absolute inset-0 [&_.bb-placeholder-tag]:hidden [&_.bb-sidetag]:hidden">{d.node}</div> })) },
    texture && { id: "tecido", title: "Tecido", items: [{ key: FABRIC, label: "Tecido real", node: <img src={textureSrc(texture, 240)} alt="" className="h-full w-full object-cover" /> }] },
  ].filter(Boolean);
  // Deslizar (telemóvel): percorre o desenho e as fotografias, por ordem.
  const order = [...photos.map((p) => p.id), ...(hasDesign ? [keyOf(list[0].id)] : [])];
  const go = (d) => { const i = Math.max(0, order.indexOf(activeKey)); setView(order[(i + d + order.length) % order.length]); };
  const activeKey = showDesign ? keyOf(activeDesign.id) : showFabric ? FABRIC : current.id;
  return (
    <div className={STICKY}>
      <div className="relative overflow-hidden rounded-3xl bg-[linear-gradient(165deg,#F6F1E9_0%,#ECE4D8_100%)] shadow-[0_0_0_1px_rgba(64,55,49,.06)] max-lg:aspect-[4/5] max-lg:max-h-[58vh] max-lg:w-full max-lg:rounded-none lg:h-[clamp(460px,calc(100vh-21rem),860px)]" data-testid="product-gallery-main" onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }} onTouchEnd={(e) => { if (touchX.current === null) return; const dx = e.changedTouches[0].clientX - touchX.current; touchX.current = null; if (Math.abs(dx) > 45) go(dx < 0 ? 1 : -1); }}>
        {/* Telemóvel: o desenho fica entre os botões do topo e as pastilhas de baixo (só aqui; nas miniaturas ocupa tudo). */}
        {showDesign ? (
          <div key={activeDesign.id} className="absolute inset-0 max-lg:[&_[data-testid=product-designer]>svg]:box-border max-lg:[&_[data-testid=product-designer]>svg]:py-14">{activeDesign.node}</div>
        ) : showFabric ? (
          <Fade k={`${FABRIC}-${fabricTexture}`}><FabricView texture={fabricTexture} name={fabricName} /></Fade>
        ) : (
          <Fade k={current.id}>
            {/* A fotografia inteira (sem cortes), sobre o seu próprio desfoque; clicar abre-a em grande, com zoom */}
            <img src={imageSrc(current.id, 400)} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full scale-110 object-cover opacity-70 blur-2xl" />
            <button type="button" onClick={() => setLightbox(photos.findIndex((p) => p.id === current.id))} aria-label="Ver a fotografia em grande" data-testid="gallery-photo-open" className="absolute inset-0 cursor-zoom-in">
              <img src={imageSrc(current.id, 1200)} alt={current.alt || product.name} className="relative h-full w-full object-contain" />
            </button>
          </Fade>
        )}

        {/* O tecido aplicado agora: pequeno, em baixo à esquerda, para não tapar a peça (o prendedor fica no topo) */}
        {fabricTexture && !isPack(product) && (showDesign || showFabric) && (
          <div className="pointer-events-none absolute bottom-3 left-3 z-10 flex items-center gap-2 rounded-full bg-ivory/85 py-1 pl-1 pr-3.5 shadow-[0_2px_12px_rgba(64,55,49,.08)] backdrop-blur-md lg:bottom-4 lg:left-4" data-testid="gallery-fabric-chip" aria-live="polite">
            <img key={fabricTexture} src={textureSrc(fabricTexture, 240)} alt="" className="bb-pop h-7 w-7 shrink-0 rounded-full object-cover ring-1 ring-white" />
            <span key={fabricName} className="bb-swap block max-w-[10rem] truncate text-xs font-medium text-ink lg:max-w-[14rem] lg:text-[13px]" data-testid="gallery-fabric-name">{hovering ? "A ver: " : ""}{fabricName ?? "Escolhe um tecido"}</span>
          </div>
        )}

        {/* Comparar simulação e fotografia original */}
        {photos.length > 0 && hasDesign && (
          <button type="button" onClick={compare} data-testid="gallery-compare" className="absolute right-3 top-3 z-10 inline-flex min-h-[40px] items-center gap-2 rounded-full bg-ivory/85 px-4 text-xs font-medium text-ink shadow-[0_2px_14px_rgba(64,55,49,.08)] ring-1 ring-ink/10 backdrop-blur-md transition-colors hover:bg-ivory lg:right-5 lg:top-5">
            {showDesign || showFabric ? "Ver original" : "Ver simulação"}
          </button>
        )}

        {!showDesign && !showFabric && (
          <span className="pointer-events-none absolute max-lg:hidden bottom-3 left-3 z-10 rounded-full bg-ivory/85 px-3 py-1 text-[11px] font-medium text-taupe-strong backdrop-blur-md lg:bottom-4 lg:left-4">Fotografia real</span>
        )}
        {photos.length > 0 && (
          <button type="button" data-testid="gallery-photos-chip" onClick={nextPhoto} className="absolute bottom-2.5 right-2.5 z-10 min-h-[40px] rounded-full bg-ivory/90 px-3.5 text-[11px] font-medium text-ink shadow-sm ring-1 ring-ink/10 lg:hidden">
            {showDesign || showFabric ? `Fotos (${photos.length})` : <span className="inline-flex items-center gap-1">{Math.max(0, order.indexOf(activeKey)) + 1} / {order.length}<ChevronRight className="h-3.5 w-3.5" aria-hidden="true" /></span>}
          </button>
        )}
      </div>

      {order.length > 1 && order.length <= 6 && (
        <div className="hidden" aria-hidden="true" data-testid="gallery-dots">
          {order.map((k) => <span key={k} className={`h-1.5 rounded-full bg-taupe transition-all ${k === activeKey ? "w-5 opacity-90" : "w-1.5 opacity-30"}`} />)}
        </div>
      )}

      {showDesign && <p className="mt-2 text-center text-[11px] text-taupe max-lg:hidden" data-testid="design-disclaimer">Pré-visualização aproximada: o padrão pode variar ligeiramente.</p>}

      {lightbox !== null && <Lightbox photos={photos} index={lightbox} onIndex={setLightbox} onClose={() => setLightbox(null)} productName={product.name} />}

      {/* Vistas, separadas por função */}
      {groups.length > 0 && (
        <div className="-mx-2 mt-2 flex items-end gap-x-5 overflow-x-auto px-2 pb-3 pt-3 [scrollbar-width:none] max-lg:-mx-5 max-lg:px-5 [&::-webkit-scrollbar]:hidden" role="tablist" aria-label="Vistas do produto" data-testid="gallery-thumbs">
          {groups.map((g, gi) => (
            <div key={g.id} className={`shrink-0 ${gi > 0 ? "border-l border-ink/10 pl-5" : ""}`}>
              <p className="mb-2 whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.18em] text-taupe-strong">{g.title}</p>
              <div className="flex gap-2.5">
                {g.items.map((t) => {
                  const on = activeKey === t.key;
                  return (
                    <button key={t.key} type="button" role="tab" aria-selected={on} data-testid={`gallery-thumb-${t.key}`} onClick={() => setView(t.key)} className="group flex w-[72px] flex-col items-center gap-1.5 text-center">
                      <span className={`relative block aspect-square w-full overflow-hidden rounded-xl bg-linen transition-all duration-300 ${on ? "shadow-md ring-2 ring-bordeaux ring-offset-2 ring-offset-ivory" : "opacity-80 ring-1 ring-ink/10 group-hover:opacity-100"}`}>
                        {t.node}
                      </span>
                      <span className={`text-[11px] leading-tight ${on ? "font-semibold text-bordeaux" : "text-taupe"}`}>{t.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
